// Zonpositie en kloktijd-helpers.
//
// Het ontwerp (§3.1) eist dat alle gebruikersinvoer en weergave in
// Europe/Amsterdam-kloktijd is, inclusief zomertijd. SunCalc rekent met
// JavaScript Date-objecten die intern UTC zijn — we moeten dus de
// kloktijd → UTC conversie expliciet doen, met DST-correctie.
//
// We doen dat zonder externe dependency via Intl.DateTimeFormat:
//  1. neem de kloktijd alsof het UTC was (een "naive" Date),
//  2. vraag Intl wat de NL-offset *zou* zijn voor die datum,
//  3. corrigeer.
//
// Op overgangsmomenten van zomertijd (laatste zo. maart / okt.) is er
// een grijs gebied van één uur waar de inverse-conversie ambigu is.
// Voor onze use-case (schaduwanalyse op een willekeurig moment) is een
// minuut-precieze fout op die twee dagen volstrekt acceptabel.

import SunCalc from "suncalc";

export interface SunPosition {
  /** Azimuth in radialen (0 = zuiden, positief richting westen) — zoals SunCalc. */
  azimuth: number;
  /** Elevatie boven de horizon in radialen. */
  altitude: number;
}

/**
 * NL-kloktijd → UTC Date. Maand is 0-gebaseerd (zoals JS Date).
 */
export function nlClockToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second = 0,
): Date {
  // Eerste gok: doe alsof de kloktijd UTC is.
  const guessUtcMs = Date.UTC(year, month, day, hour, minute, second);
  // Bereken hoeveel offset Amsterdam *toen* had ten opzichte van UTC.
  const offsetMin = nlOffsetMinutes(new Date(guessUtcMs));
  // Echte UTC = naive UTC − offset.
  return new Date(guessUtcMs - offsetMin * 60_000);
}

/**
 * Offset (in minuten) van Europe/Amsterdam t.o.v. UTC voor een gegeven moment.
 * Positief in zomertijd (CEST, +120) en wintertijd (CET, +60).
 */
export function nlOffsetMinutes(at: Date): number {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Amsterdam",
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = fmt.formatToParts(at);
  const get = (type: string): number => {
    const p = parts.find((x) => x.type === type);
    return p ? Number(p.value) : 0;
  };
  const localUtcEquivalent = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  return Math.round((localUtcEquivalent - at.getTime()) / 60_000);
}

/**
 * Bereken zonstand voor een moment + locatie.
 * `date` is een echte UTC Date; gebruik {@link nlClockToUtc} om uit kloktijd
 * te starten.
 */
export function getSunPosition(date: Date, lat: number, lng: number): SunPosition {
  const p = SunCalc.getPosition(date, lat, lng);
  return { azimuth: p.azimuth, altitude: p.altitude };
}

/**
 * Zon-vector in een lokaal ENU-stelsel (Oost, Noord, Op).
 * Geeft de richting *waar de zon vandaan komt*.
 *
 * SunCalc gebruikt: azimuth = 0 → zuid, +π/2 → west, −π/2 → oost, ±π → noord.
 * We zetten dat om naar ENU: x=oost, y=noord, z=omhoog.
 */
export function sunDirectionEnu(sun: SunPosition): { x: number; y: number; z: number } {
  const cosAlt = Math.cos(sun.altitude);
  const sinAlt = Math.sin(sun.altitude);
  // Met azimuth=0 (zuid): x_oost=0, y_noord=−1.
  // Met azimuth=π/2 (west): x_oost=−1, y_noord=0.
  const x = -cosAlt * Math.sin(sun.azimuth);
  const y = -cosAlt * Math.cos(sun.azimuth);
  const z = sinAlt;
  return { x, y, z };
}

/** Zonsopkomst en -ondergang in NL-kloktijd voor een specifieke datum + locatie. */
export function getSunTimesNl(
  year: number,
  month: number,
  day: number,
  lat: number,
  lng: number,
): { sunriseMin: number; sunsetMin: number } {
  // Pak het moment waarop het in NL 12:00 is — SunCalc kiest dan de juiste dag.
  const noon = nlClockToUtc(year, month, day, 12, 0);
  const t = SunCalc.getTimes(noon, lat, lng);
  return {
    sunriseMin: utcDateToNlMinutes(t.sunrise, year, month, day),
    sunsetMin: utcDateToNlMinutes(t.sunset, year, month, day),
  };
}

/**
 * Zet een UTC-Date om naar minuten-na-middernacht in NL-kloktijd voor een
 * gegeven datum (referentiedag — handig bij sliders).
 */
function utcDateToNlMinutes(date: Date, refY: number, refM: number, refD: number): number {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Amsterdam",
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const parts = fmt.formatToParts(date);
  const get = (type: string): number => Number(parts.find((p) => p.type === type)!.value);
  const y = get("year");
  const m = get("month");
  const d = get("day");
  const hh = get("hour");
  const mm = get("minute");
  // Uitdrukken in minuten-na-middernacht van de referentiedag, ook als de
  // gebeurtenis (zonsopkomst/ondergang) op aangrenzende dag valt.
  const dayDiff =
    Math.round(
      (Date.UTC(y, m - 1, d) - Date.UTC(refY, refM, refD)) / (24 * 3600_000),
    );
  return dayDiff * 24 * 60 + hh * 60 + mm;
}

/** Aantal dagen in een specifiek jaar (rekening houdend met schrikkeljaren). */
export function daysInYear(year: number): 365 | 366 {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365;
}

/** Zet day-of-year (1-based) om naar { month0, day }. Maand is 0-gebaseerd. */
export function dayOfYearToMonthDay(year: number, dayOfYear: number): { month: number; day: number } {
  const d = new Date(Date.UTC(year, 0, dayOfYear));
  return { month: d.getUTCMonth(), day: d.getUTCDate() };
}

/** Inverse: { month0, day } → day-of-year (1-based). */
export function monthDayToDayOfYear(year: number, month: number, day: number): number {
  const start = Date.UTC(year, 0, 1);
  const target = Date.UTC(year, month, day);
  return Math.round((target - start) / (24 * 3600_000)) + 1;
}
